import React from "react";

const BaseEntityDoc = () => {
  return (
    <section className="mb-12 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        Domain Layer — Complete Entity Documentation
      </h2>

      <p className="mb-6">
        The AxisNet Domain Layer defines the core business entities supporting
        identity, authentication, authorization (RBAC & PBAC), verification,
        session lifecycle management, and diagnostic logging. Each entity is
        persistence-agnostic and structured following Domain-Driven Design.
      </p>

      {/* USER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          User — Primary System Identity
        </h3>

        <p className="mb-4">
          The <strong>User</strong> entity stores authentication credentials,
          account state, security flags, and relational mappings to roles,
          permissions, sessions, and logs.
        </p>

        <h4 className="text-lg font-semibold text-white mt-4 mb-2">
          Entity Structure (with XML Comments)
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`/// <summary>
/// Represents a system user entity that contains authentication,
/// account status, and basic profile information.
/// </summary>
public class User : Entity<Guid>
{
    /// <summary>The user's first name.</summary>
    public string Name { get; set; } = string.Empty;

    /// <summary>The user's last name (surname).</summary>
    public string Surname { get; set; } = string.Empty;

    /// <summary>Unique username used for login or identification.</summary>
    public string Username { get; set; } = string.Empty;

    /// <summary>The user's email address, used for login and communication.</summary>
    public string Email { get; set; } = string.Empty;

    /// <summary>The user's password (stored as a hashed value).</summary>
    public string Password { get; set; } = string.Empty;

    /// <summary>Indicates whether the user's email has been confirmed.</summary>
    public bool IsEmailConfirmed { get; set; }

    /// <summary>Determines whether the user account is currently active.</summary>
    public bool IsActive { get; set; } = true;

    /// <summary>Indicates whether the user is temporarily locked.</summary>
    public bool IsLocked { get; set; } = false;

    /// <summary>The date until which the account remains locked.</summary>
    public DateTime? LockedUntil { get; set; }

    /// <summary>Indicates whether the user has two-factor authentication enabled.</summary>
    public bool IsTwoFactorEnabled { get; set; } = false;

    // Navigation
    public List<Log>? Logs { get; set; }
    public List<Session>? Sessions { get; set; }
    public List<UserRole>? UserRoles { get; set; }
    public List<UserPermission>? UserPermissions { get; set; }
}`}
        </pre>
      </div>

      {/* ROLE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Role — Authorization Group
        </h3>

        <p className="mb-4">
          The <strong>Role</strong> entity represents a bundle of permissions
          assigned to users for RBAC-based authorization.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Entity Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`/// <summary>
/// Represents a user role within the system.
/// </summary>
public class Role : Entity<Guid>
{
    /// <summary>The display name of the role.</summary>
    public string Name { get; set; } = string.Empty;

    /// <summary>A unique key for identifying the role in code.</summary>
    public string Key { get; set; } = string.Empty;

    /// <summary>Description explaining the purpose of the role.</summary>
    public string? Description { get; set; }

    /// <summary>Collection mapping users to this role.</summary>
    public ICollection<UserRole> UserRoles { get; set; } = new List<UserRole>();
}`}
        </pre>
      </div>

      {/* PERMISSION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Permission — Fine-Grained Access Control
        </h3>

        <p className="mb-4">
          The <strong>Permission</strong> entity defines a specific system
          action that may be granted through roles or directly to users.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Entity Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`/// <summary>
/// Represents a specific permission that defines an allowed action.
/// </summary>
public class Permission : Entity<Guid>
{
    /// <summary>Display name of the permission.</summary>
    public string Name { get; set; } = string.Empty;

    /// <summary>Unique system key (e.g. "user.view").</summary>
    public string Key { get; set; } = string.Empty;

    /// <summary>Description of what this permission allows.</summary>
    public string? Description { get; set; }

    /// <summary>Mappings of users who directly own this permission.</summary>
    public ICollection<UserPermission> UserPermissions { get; set; } = new List<UserPermission>();
}`}
        </pre>
      </div>

      {/* USER ROLE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          UserRole — Many-to-Many Role Mapping
        </h3>

        <p className="mb-4">
          <strong>UserRole</strong> links users and roles through a many-to-many
          relationship.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Entity Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`public class UserRole : Entity<Guid>
{
    /// <summary>The user ID.</summary>
    public Guid UserId { get; set; }

    /// <summary>The role ID.</summary>
    public Guid RoleId { get; set; }

    /// <summary>Reference to the related user.</summary>
    public User User { get; set; } = null!;

    /// <summary>Reference to the related role.</summary>
    public Role Role { get; set; } = null!;
}`}
        </pre>
      </div>

      {/* USER PERMISSION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          UserPermission — Direct Permission Assignment
        </h3>

        <p className="mb-4">
          <strong>UserPermission</strong> maps users to direct permissions,
          allowing PBAC-style granular access control.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Entity Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`public class UserPermission : Entity<Guid>
{
    /// <summary>The user ID.</summary>
    public Guid UserId { get; set; }

    /// <summary>The permission ID.</summary>
    public Guid PermissionId { get; set; }

    /// <summary>Reference to the user.</summary>
    public User User { get; set; } = null!;

    /// <summary>Reference to the permission.</summary>
    public Permission Permission { get; set; } = null!;
}`}
        </pre>
      </div>

      {/* SESSION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Session — Authentication & Device Tracking
        </h3>

        <p className="mb-4">
          The <strong>Session</strong> entity captures login activity, token
          lifecycle, device metadata, browser details, logout info, and
          geolocation.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Entity Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`/// <summary>
/// Represents an active user session.
/// </summary>
public class Session : Entity<Guid>
{
    public Guid UserId { get; set; }
    public string? RefreshToken { get; set; }
    public DateTime? AccessTokenExpiresAt { get; set; }
    public DateTime? RefreshTokenExpiresAt { get; set; }
    public string AccessToken { get; set; } = string.Empty;
    public string? IpAddress { get; set; }
    public string? Device { get; set; }
    public string? OperatingSystem { get; set; }
    public string? Browser { get; set; }
    public string? Location { get; set; }
    public string? UserAgent { get; set; }
    public string? SessionKey { get; set; }
    public DateTime LoginAt { get; set; } = DateTime.UtcNow;
    public DateTime? LogoutAt { get; set; }
    public double? Latitude { get; set; }
    public double? Longitude { get; set; }
    public string? LogoutIpAddress { get; set; }
    public string? LogoutDevice { get; set; }

    // Navigation
    public User? User { get; set; }
}`}
        </pre>
      </div>

      {/* LOG */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Log — System Activity & Error Tracking
        </h3>

        <p className="mb-4">
          The <strong>Log</strong> entity stores detailed error, diagnostic, and
          performance information for auditing and monitoring.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Entity Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`/// <summary>
/// Represents a system log entry storing diagnostic, error, and activity data.
/// </summary>
public class Log : Entity<Guid>
{
    public LogStatusEnum Level { get; set; } = LogStatusEnum.Information;
    public string? Category { get; set; }
    public string Message { get; set; } = string.Empty;
    public string? Exception { get; set; }
    public string? Source { get; set; }
    public Guid? UserId { get; set; }
    public string? IpAddress { get; set; }
    public string? RequestPath { get; set; }
    public string? RequestMethod { get; set; }
    public int? ResponseStatusCode { get; set; }
    public int? ExecutionTimeMs { get; set; }
    public string? Data { get; set; }
    public string? Environment { get; set; }

    // Navigation
    public User? User { get; set; }
}`}
        </pre>
      </div>

      {/* VERIFICATION CODE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          VerificationCode — Secure OTP Lifecycle
        </h3>

        <p className="mb-4">
          The <strong>VerificationCode</strong> entity handles secure OTP logic
          with hashed values, expiry tracking, attempt limits, and usage flags.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Entity Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`public class VerificationCode : Entity<Guid>
{
    public Guid UserId { get; set; }
    public bool IsUsed { get; set; }
    public string? HashCode { get; set; }
    public DateTime? UsedAt { get; set; }
    public int AttemptCount { get; set; }
    public DateTime ExpiryAt { get; set; }
    public VerificationCodeEnum Type { get; set; }

    // Navigation
    public User? User { get; set; }
}`}
        </pre>
      </div>
    </section>
  );
};

export default BaseEntityDoc;
